import Placeholder from "@/components/Placeholder";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

export function About() {
  return (
    <div className="max-w-[1200px] mx-auto px-6 py-16">
      <div className="label-mono text-[#89b4fa]">/// 01 · ABOUT THE NATION</div>
      <h1 className="font-display text-5xl mt-3">Kasabonika Lake First Nation</h1>
      <p className="text-white/70 mt-6 max-w-2xl leading-relaxed">
        A remote Oji-Cree First Nation on the shores of Kasabonika Lake in Northern Ontario, Canada. Member of Nishnawbe-Aski Nation (NAN) within Treaty 9. Governed by an elected Chief and Council under a two-year custom-electoral term.
      </p>
      <div className="grid md:grid-cols-3 gap-4 mt-12">
        {[
          ["Territory", "Treaty 9 · Nishnawbe-Aski Nation"],
          ["Language", "Oji-Cree · Anishininiimowin"],
          ["Governance", "Elected Chief & Council · Custom Electoral"],
          ["Location", "Kasabonika Lake, Ontario"],
          ["Postal", "P0V 1Y0"],
          ["Council Term", "2026 — 2028"],
        ].map(([k, v]) => (
          <div key={k} className="border border-white/10 p-5">
            <div className="label-mono">{k}</div>
            <div className="font-display text-xl mt-1">{v}</div>
          </div>
        ))}
      </div>

      <h2 className="font-display text-3xl mt-16">Chief & Council · 2026 — 2028</h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
        {[
          ["Chief", "Matthias Anderson"],
          ["Deputy Chief", "Terry Stoney"],
          ["Head Councillor", "Rachel Mamakwa"],
          ["Councillor", "Mike Morris"],
          ["Councillor", "Josie Anderson Sr"],
          ["Councillor", "Simon Semple"],
          ["Councillor", "Chantel Stoney"],
          ["Councillor", "Cassidy Anderson"],
        ].map(([r, n]) => (
          <div key={n} className="border border-white/10 p-4 flex flex-col">
            <Placeholder label={`insert ${n.toLowerCase()} portrait`} height="h-28" />
            <div className="label-mono mt-3">{r}</div>
            <div className="font-display text-lg mt-1">{n}</div>
            <div className="placeholder-box h-24 mt-3 text-left items-start justify-start !p-3 flex-1">
              [ add description / general information here ]
            </div>
          </div>
        ))}
      </div>

      <h2 className="font-display text-3xl mt-16">Senior Administration</h2>
      <div className="grid md:grid-cols-2 gap-3 mt-6 max-w-2xl">
        {[
          ["Executive Director", "Blade Anderson"],
          ["Capital Projects Manager", "Keith Mason"],
        ].map(([r, n]) => (
          <div key={n} className="border border-white/10 p-4">
            <div className="label-mono">{r}</div>
            <div className="font-display text-lg mt-1">{n}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function History() {
  const timeline = [
    ["Time immemorial", "Oji-Cree presence on the land through clan governance and Anishininiimowin oral tradition."],
    ["1905 – 1906", "Treaty 9 (James Bay Treaty) signed between the Crown and Anishinaabe / Cree nations of Northern Ontario."],
    ["1977", "Nishnawbe-Aski Nation (NAN) established as the political territorial organization representing Treaty 9 First Nations."],
    ["1985", "Bill C-31 restores membership rights."],
    ["2026", "Current Chief & Council elected · 2-year term begins."],
  ];
  return (
    <div className="max-w-[1200px] mx-auto px-6 py-16">
      <div className="label-mono text-[#d4b876]">/// 02 · HISTORY</div>
      <h1 className="font-display text-5xl mt-3">A continuous story</h1>
      <div className="mt-12 space-y-6">
        {timeline.map(([year, text]) => (
          <div key={year} className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-6 border-l-2 border-[#89b4fa]/40 pl-6">
            <div className="font-display text-2xl text-[#89b4fa]">{year}</div>
            <div className="text-white/75 leading-relaxed">{text}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Departments() {
  const depts = [
    ["GOV", "Governance & Administration", "Chief, Council, executive staff, band office operations."],
    ["HTH", "Health Services", "Nursing station, mental health, elder care coordination."],
    ["EDU", "Education", "K–12 support, post-secondary sponsorship, language programs."],
    ["HSG", "Housing & Public Works", "Housing allocation, water, roads, community infrastructure."],
    ["CAP", "Capital Projects", "Major infrastructure — led by Keith Mason, Capital Projects Manager."],
    ["LND", "Lands & Resources", "Traditional territory, treaty rights, environmental monitoring."],
    ["SOC", "Social Services", "Family support, income assistance, child welfare liaison."],
    ["ECO", "Economic Development", "Nation-owned enterprises, employment, capital projects."],
    ["CUL", "Culture & Language", "Anishininiimowin revitalization, ceremony support, archives."],
  ];
  const [directory, setDirectory] = useState({});
  useEffect(() => {
    // Public route — this endpoint requires a signed-in verified member, so it
    // silently returns nothing for anonymous visitors. That is deliberate.
    api.get("/hrm/staff-directory").then(r => setDirectory(r.data)).catch(()=>{});
  }, []);
  return (
    <div className="max-w-[1400px] mx-auto px-6 py-16">
      <div className="label-mono">03 · Departments</div>
      <h1 className="font-display text-5xl mt-3 leading-tight">Operational structure</h1>
      <p className="text-white/60 mt-4 max-w-2xl">
        Nine departments deliver Nation services. Staff contact details are visible to signed-in members
        through the automatic HR-fed directory below each department.
      </p>
      <div className="grid md:grid-cols-2 gap-4 mt-12">
        {depts.map(([code, name, desc]) => {
          const staff = directory[code] || [];
          return (
            <div key={code} className="border border-white/8 bg-white/[0.015] p-6" data-testid={`dept-${code}`}>
              <div className="flex items-center justify-between">
                <div className="font-mono text-[10px] text-[color:var(--cobalt)] tracking-widest uppercase">{code}</div>
                {staff.length > 0 && <span className="font-mono text-[10px] text-white/40 uppercase tracking-widest">{staff.length} on staff</span>}
              </div>
              <div className="font-display text-2xl mt-2">{name}</div>
              <div className="text-white/60 text-sm mt-3 leading-relaxed">{desc}</div>
              {staff.length > 0 && (
                <div className="mt-5 pt-5 border-t border-white/8 space-y-2">
                  {staff.map(s => (
                    <div key={s.id} className="text-sm">
                      <div className="text-white/90">{s.name}</div>
                      <div className="font-mono text-[10px] text-white/50 uppercase tracking-widest">
                        {s.position_title}
                        {s.work_email && <span> · {s.work_email}</span>}
                        {s.work_phone && <span> · {s.work_phone}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function Contact() {
  return (
    <div className="max-w-[1200px] mx-auto px-6 py-16">
      <div className="label-mono text-[#89b4fa]">/// 05 · CONTACT</div>
      <h1 className="font-display text-5xl mt-3">Reach the band office</h1>
      <div className="grid md:grid-cols-2 gap-8 mt-12">
        <div className="border border-white/10 p-6 space-y-3 font-mono text-sm">
          <div><span className="label-mono block">Mailing Address</span>General Delivery<br/>Kasabonika Lake First Nation<br/>Ontario, Canada · P0V 1Y0</div>
          <div><span className="label-mono block">Phone</span>(807) 535-2547</div>
          <div><span className="label-mono block">Fax</span>(807) 535-1152</div>
          <div><span className="label-mono block">Registry Clerk</span>clerk@klfn.ca</div>
          <div><span className="label-mono block">Chief&apos;s Office</span>chief@klfn.ca</div>
          <div><span className="label-mono block">Executive Director</span>director@klfn.ca</div>
        </div>
        <Placeholder label="insert band office photo / community map" height="h-full min-h-[280px]" />
      </div>
    </div>
  );
}
